import axios from "axios";
import axiosRetry from "axios-retry";
import type { FileTag } from "@remi/enums/file-tag";
import type { FileType } from "@remi/types/file";

import { displayPendingToast } from "~/components/ui/sonner";

const FILE_DOWNLOAD_RETRY_DELAY_FACTOR_MS = 500;

export const fileDownloadClient = axios.create();
axiosRetry(fileDownloadClient, {
  retries: 3,
  retryDelay: (count, error) => axiosRetry.exponentialDelay(count, error, FILE_DOWNLOAD_RETRY_DELAY_FACTOR_MS),
});

const HANDLED_DOWNLOAD_FAILURE_STATUSES = new Set([403, 404, 429, 500, 502, 503, 504]);

export const isHandledDownloadFailure = (status: number | undefined): boolean =>
  status !== undefined && HANDLED_DOWNLOAD_FAILURE_STATUSES.has(status);

type Person = {
  id: string;
  contact: { name: string };
};

export type ListFile<P extends Person = Person> = {
  id: string;
  name: string;
  type: FileType;
  tags: { tagId: FileTag }[];
  aiSummary?: string | null;
  person?: P | null;
  createdAt: Date;
  url: string;
  thumbnailUrl?: string;
  deletable: boolean;
  deliveredToXa: boolean;
  xaDeliveredAt: Date | null;
  bidQuoteForPdf?: {
    id: string;
    requestForQuoteActive?: {
      id: number;
      activeQuoteId: string | null;
    };
  } | null;
};

const getCreatedAtMs = (file: ListFile): number => {
  if (file.createdAt instanceof Date) {
    return file.createdAt.getTime();
  }

  const parsed = new Date(file.createdAt).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const displayOrder = (a: ListFile, b: ListFile) => {
  if (a.bidQuoteForPdf !== null && b.bidQuoteForPdf !== null) {
    const aIsActive = !!a.bidQuoteForPdf?.requestForQuoteActive;
    const bIsActive = !!b.bidQuoteForPdf?.requestForQuoteActive;

    if (aIsActive && !bIsActive) {
      return -1;
    }
    if (!aIsActive && bIsActive) {
      return 1;
    }

    return getCreatedAtMs(b) - getCreatedAtMs(a);
  }

  if (a.bidQuoteForPdf !== null) {
    return -1;
  }
  if (b.bidQuoteForPdf !== null) {
    return 1;
  }

  return getCreatedAtMs(b) - getCreatedAtMs(a);
};

const DOWNLOAD_BLOB_MIME = {
  PHOTO: "image/jpeg",
  VIDEO: "video/mp4",
  AUDIO: "audio/mpeg",
  DOCUMENT: "application/pdf",
} as const satisfies Record<FileType, string>;

export const downloadFile = (file: { url: string; name: string; type: FileType }) => {
  const download = async () => {
    const res = await fileDownloadClient.get(file.url, { responseType: "blob" });
    // biome-ignore lint/plugin/no-type-assertions: bypass lint check for type assertions
    const content = new File([res.data as BlobPart], file.name, {
      type: DOWNLOAD_BLOB_MIME[file.type],
    });
    const url = window.URL.createObjectURL(content);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", file.name);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };
  const promise = download();
  displayPendingToast({
    loading: "Downloading file...",
    success: "File downloaded successfully!",
    error: "Error downloading file",
    promise,
  });
};
